import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [nova, setNova] = useState("");
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Token não informado"); return; }
    api.get(`/auth/reset-info/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Token inválido"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (nova.length < 4) return toast.error("Senha deve ter no mínimo 4 caracteres");
    if (nova !== conf) return toast.error("Senhas não conferem");
    setSaving(true);
    try {
      await api.post("/auth/reset-with-token", { token, nova_senha: nova });
      setDone(true);
      toast.success("Senha redefinida com sucesso");
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao redefinir");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-white p-4">
      <Card className="w-full max-w-md shadow-xl border-orange-100" data-testid="reset-card">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-orange-600"/>Redefinir senha
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-4" data-testid="reset-error">
              <XCircle className="w-12 h-12 mx-auto text-rose-500 mb-3"/>
              <p className="font-bold text-rose-700">{error}</p>
              <p className="text-sm text-muted-foreground mt-2">Solicite ao administrador um novo link.</p>
              <Link to="/login" className="text-orange-600 text-sm mt-4 inline-block underline">Voltar para o login</Link>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3"/>
              <p className="font-bold text-emerald-700">Senha redefinida!</p>
              <p className="text-sm text-muted-foreground mt-2">Redirecionando para o login...</p>
            </div>
          ) : !info ? (
            <div className="text-center text-muted-foreground py-4">Validando token...</div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                <div><b>Usuário:</b> {info.user?.nome}</div>
                <div className="text-muted-foreground">{info.user?.email}</div>
              </div>
              <div><Label>Nova senha</Label>
                <Input type="password" value={nova} onChange={(e) => setNova(e.target.value)} data-testid="reset-nova" autoFocus/></div>
              <div><Label>Confirmar nova senha</Label>
                <Input type="password" value={conf} onChange={(e) => setConf(e.target.value)} data-testid="reset-conf"/></div>
              <Button type="submit" disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="reset-submit">
                {saving ? "Salvando..." : "Redefinir senha"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
